import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../i18n';
import styles from './MyResources.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function MyResources() {
  const { t } = useLanguage();
  const { currentUser, loading: authLoading } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser) return;

    const fetchSavedResources = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/resources/saved`, {
          headers: {
            'x-user-id': currentUser.uid
          }
        });

        if (!res.ok) {
          throw new Error(`Failed to load saved resources: ${res.status}`);
        }

        const data = await res.json();
        setResources(data);
      } catch (err) {
        setError(err.message || 'Could not load your saved resources');
      } finally {
        setLoading(false);
      }
    };

    fetchSavedResources();
  }, [currentUser]);

  const handleToggleSave = async (resourceId, currentlySaved) => {
    if (!currentUser) return;

    try {
      const res = await fetch(`${API_URL}/resources/${resourceId}/toggle-save`, {
        method: 'POST',
        headers: {
          'x-user-id': currentUser.uid
        }
      });

      if (!res.ok) throw new Error('Toggle save failed');

      // Refresh list
      setResources(prev => 
        currentlySaved 
          ? prev.filter(r => r.id !== resourceId)
          : [...prev] // backend handles add, refresh fetches again if needed
      );

      // Optional: full refresh after toggle
      // fetchSavedResources();
    } catch (err) {
      console.error('Toggle save error:', err);
      alert('Failed to update saved status');
    }
  };

  if (authLoading || loading) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <Icon icon="lucide:loader-2" className={styles.spinner} />
          {t('loadingSavedResources') || 'Loading your saved resources...'}
          </div>
        </div>
      </main>
    );
  }

  if (!currentUser) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.error}>
            {t('pleaseSignInToViewSaved') || 'Please sign in to view your saved resources.'}
          </div>
          <Link to="/login" className={styles.btnPrimary}>
                        {t('signIn')}
          </Link>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.error}>
            <Icon icon="lucide:alert-triangle" />
            {error}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <Link to="/" className={styles.backLink}>
            <Icon icon="lucide:arrow-left" />
            {t('backToHome')}
          </Link>
          <h1 className={styles.pageTitle}>{t('myResources')}</h1>
          <p className={styles.subtitle}>
            {t('yourSavedBookmarks', { /* optional */ }) || 'Your saved and bookmarked resources for quick access.'}
          </p>
        </header>

        {resources.length === 0 ? (
          <div className={styles.empty}>
            <Icon icon="lucide:bookmark" className={styles.emptyIcon} />
            <p>{t('noSavedResources') || "You haven't saved any resources yet."}</p>
            <Link to="/collections" className={styles.exploreBtn}>
                          {t('exploreCollections') || 'Explore Collections'}
            </Link>
          </div>
        ) : (
          <div className={styles.resourcesGrid}>
            {resources.map((res) => (
              <div key={res.id} className={styles.resourceCard}>
                <Link to={`/resource/${res.id}`} className={styles.cardLink}>
                  <div className={styles.cardImagePlaceholder}></div>

                  <div className={styles.cardContent}>
                    <span className={styles.cardType}>{res.type}</span>
                    <h3 className={styles.cardTitle}>{res.title}</h3>
                    <p className={styles.cardDescription}>
                      {res.description.length > 100
                        ? res.description.substring(0, 100) + '...'
                        : res.description}
                    </p>

                    <div className={styles.cardMeta}>
                      <span>From: {res.source || 'Community'}</span>
                      <span>
                        {res.rating ? `${res.rating} (${res.reviewCount || 0})` : 'No ratings'}
                      </span>
                      <span>{res.languages?.join(', ') || 'N/A'}</span>
                    </div>
                  </div>
                </Link>

                <div className={styles.cardActions}>
                  <button
                    className={styles.actionBtn}
                    onClick={() => handleToggleSave(res.id, true)}
                  >
                    <Icon icon="lucide:bookmark-minus" />
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
// import { Link } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './MyResources.module.css';

// // Dummy data - in real app this would come from state/API
// const dummyResources = [
//   {
//     id: 1,
//     title: "Digital Security Checklist for High-Risk Activists (2026)",
//     type: "Guide",
//     category: "Digital Security",
//     added: "Jan 15, 2026",
//     lastAccessed: "3 days ago",
//     source: "Global Rights Foundation",
//     tags: ["Checklist", "High-Risk", "Device Security"],
//     link: "/resource/123"
//   },
//   {
//     id: 2,
//     title: "Organizational Security Policy Template",
//     type: "Template",
//     category: "Policy",
//     added: "Dec 28, 2025",
//     lastAccessed: "1 week ago",
//     source: "SafeGuard Hub",
//     tags: ["Policy", "Organization", "Emergency"],
//     link: "/resource/456"
//   },
//   {
//     id: 3,
//     title: "Emergency Digital Security Helpdesk Contacts",
//     type: "Contact",
//     category: "Support",
//     added: "Feb 2, 2026",
//     lastAccessed: "Yesterday",
//     source: "Front Line Defenders",
//     tags: ["24/7", "Helpdesk", "Incident Response"],
//     link: "/support"
//   }
// ];

// export default function MyResources() {
//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Header */}
//         <header className={styles.header}>
//           <div className={styles.headerLeft}>
//             <h1 className={styles.title}>My Resources</h1>
//             <p className={styles.subtitle}>
//               Your saved and recently accessed safeguarding materials
//             </p>
//           </div>

//           <div className={styles.headerActions}>
//             <Link to="/" className={styles.backLink}>
//               <Icon icon="lucide:arrow-left" />
//               Back to Home
//             </Link>
//             <button className={styles.exportBtn}>
//               <Icon icon="lucide:download" />
//               Export All
//             </button>
//           </div>
//         </header>

//         {/* Controls */}
//         <div className={styles.controls}>
//           <div className={styles.searchBox}>
//             <input
//               type="text"
//               placeholder="Search in your saved resources..."
//               className={styles.searchInput}
//             />
//             <Icon icon="lucide:search" className={styles.searchIcon} />
//           </div>

//           <div className={styles.sortFilter}>
//             <select className={styles.select}>
//               <option>Recently Added</option>
//               <option>Most Recently Used</option>
//               <option>A–Z</option>
//               <option>By Category</option>
//             </select>

//             <button className={styles.filterBtn}>
//               <Icon icon="lucide:filter" />
//               Filter
//             </button>
//           </div>
//         </div>

//         {/* Resources Grid */}
//         {dummyResources.length === 0 ? (
//           <div className={styles.emptyState}>
//             <Icon icon="lucide:bookmark" className={styles.emptyIcon} />
//             <h3>No saved resources yet</h3>
//             <p>Start exploring and bookmark items to see them here.</p>
//             <Link to="/collections" className={styles.emptyCta}>
//               Browse Collections
//             </Link>
//           </div>
//         ) : (
//           <div className={styles.grid}>
//             {dummyResources.map((res) => (
//               <Link to={res.link} key={res.id} className={styles.card}>
//                 <div className={styles.cardImagePlaceholder}></div>

//                 <div className={styles.cardBody}>
//                   <div className={styles.cardTags}>
//                     <span className={styles.tag}>{res.type}</span>
//                     <span className={styles.tag}>{res.category}</span>
//                   </div>

//                   <h3 className={styles.cardTitle}>{res.title}</h3>

//                   <p className={styles.cardSource}>From: {res.source}</p>

//                   <div className={styles.cardMeta}>
//                     <span>Added: {res.added}</span>
//                     <span>Last used: {res.lastAccessed}</span>
//                   </div>

//                   <div className={styles.cardTagsSmall}>
//                     {res.tags.map((tag, i) => (
//                       <span key={i} className={styles.smallTag}>{tag}</span>
//                     ))}
//                   </div>
//                 </div>

//                 <div className={styles.cardActions}>
//                   <button className={styles.actionBtn} onClick={(e) => e.preventDefault()}>
//                     <Icon icon="lucide:bookmark-minus" />
//                   </button>
//                   <button className={styles.actionBtn} onClick={(e) => e.preventDefault()}>
//                     <Icon icon="lucide:share-2" />
//                   </button>
//                 </div>
//               </Link>
//             ))}
//           </div>
//         )}

//         {/* Load more */}
//         {dummyResources.length > 0 && (
//           <div className={styles.loadMore}>
//             <button className={styles.loadMoreBtn}>
//               Load More
//               <Icon icon="lucide:arrow-down" />
//             </button>
//             <p className={styles.paginationInfo}>
//               Showing {dummyResources.length} of 38 saved items
//             </p>
//           </div>
//         )}
//       </div>
//     </main>
//   );
// }
// import { Icon } from '@iconify/react';
// import styles from './MyResources.module.css';
// import Footer from '../../components/Footer/Footer';

// export default function MyResources() {
//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Header / Controls */}
//         <header className={styles.pageHeader}>
//           <h1 className={styles.pageTitle}>My Resources</h1>
//           <p className={styles.pageSubtitle}>
//             Your saved, bookmarked, and recently accessed safeguarding resources
//           </p>

//           <div className={styles.controls}>
//             <div className={styles.searchWrapper}>
//               <input
//                 type="text"
//                 placeholder="Search in your resources..."
//                 className={styles.searchInput}
//               />
//               <Icon icon="lucide:search" className={styles.searchIcon} />
//             </div>

//             <div className={styles.sortAndFilter}>
//               <select className={styles.select}>
//                 <option>Recently Added</option>
//                 <option>Most Used</option>
//                 <option>A–Z</option>
//                 <option>By Category</option>
//               </select>

//               <button className={styles.btnOutline}>
//                 <Icon icon="lucide:filter" />
//                 Filter
//               </button>

//               <button className={styles.btnPrimary}>
//                 <Icon icon="lucide:download" />
//                 Export All
//               </button>
//             </div>
//           </div>
//         </header>

//         {/* Resource Grid */}
//         <section className={styles.resourcesGrid}>
//           {/* Example saved resource card */}
//           <article className={styles.resourceCard}>
//             <div className={styles.cardImagePlaceholder}></div>

//             <div className={styles.cardContent}>
//               <div className={styles.cardTags}>
//                 <span className={styles.tag}>Guide</span>
//                 <span className={styles.tag}>Digital Security</span>
//               </div>

//               <h3 className={styles.cardTitle}>
//                 Digital Security Checklist for High-Risk Activists (2025 Edition)
//               </h3>

//               <p className={styles.cardDescription}>
//                 Step-by-step device hardening, secure messaging setup, and threat modeling template.
//               </p>

//               <div className={styles.cardMeta}>
//                 <span>Global Rights Foundation</span>
//                 <span>Last accessed: 3 days ago</span>
//                 <span>Added: Jan 12, 2026</span>
//               </div>

//               <div className={styles.cardActions}>
//                 <button className={styles.btnPrimarySmall}>Open Resource</button>
//                 <button className={styles.btnIcon}>
//                   <Icon icon="lucide:bookmark-minus" />
//                 </button>
//                 <button className={styles.btnIcon}>
//                   <Icon icon="lucide:share-2" />
//                 </button>
//               </div>
//             </div>
//           </article>

//           {/* Repeat similar cards for more items */}
//           <article className={styles.resourceCard}>
//             <div className={styles.cardImagePlaceholder}></div>
//             <div className={styles.cardContent}>
//               <div className={styles.cardTags}>
//                 <span className={styles.tag}>Template</span>
//                 <span className={styles.tag}>Policy</span>
//               </div>
//               <h3 className={styles.cardTitle}>
//                 Organizational Security Policy Template
//               </h3>
//               <p className={styles.cardDescription}>
//                 Customizable framework for emergency protocols and incident response planning.
//               </p>
//               <div className={styles.cardMeta}>
//                 <span>SafeGuard Hub</span>
//                 <span>Last accessed: 1 week ago</span>
//                 <span>Added: Dec 28, 2025</span>
//               </div>
//               <div className={styles.cardActions}>
//                 <button className={styles.btnPrimarySmall}>Open Resource</button>
//                 <button className={styles.btnIcon}>
//                   <Icon icon="lucide:bookmark-minus" />
//                 </button>
//               </div>
//             </div>
//           </article>

//           {/* Empty state (shown when no resources) */}
//           <div className={styles.emptyState} style={{ display: 'none' }}>
//             <Icon icon="lucide:bookmark" className={styles.emptyIcon} />
//             <h3>No resources saved yet</h3>
//             <p>Browse the library and bookmark items to see them here.</p>
//             <button className={styles.btnPrimary}>
//               Browse Resources
//             </button>
//           </div>
//         </section>

//         {/* Pagination / Load more */}
//         <div className={styles.pagination}>
//           <button className={styles.loadMoreBtn}>
//             Load More Resources
//             <Icon icon="lucide:arrow-down" />
//           </button>
//           <p className={styles.paginationInfo}>Showing 1–12 of 38 saved resources</p>
//         </div>
//         <Footer/>
//       </div>

//     </main>
//   );
// }